import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Modal } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, type OptionalString, formatTimeDisplay } from './hooks/useStepDetailRender';
import EditToolbar, { useEditToolbar } from '../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../graphql/queries/getTmsTaskDetail';
import { GET_HAS_TMS_LOADING_DETAIL } from '../../graphql/queries/getHasTmsLoadingDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../graphql/queries/getTmsTaskDetail';
import {
    SAVE_TMS_LOADING_DETAIL_INFO,
    type HandleBatchTmsLoadingEventItemInput,
    type SaveTmsLoadingDetailInfoInput,
} from '../../graphql/mutations/taskMutations';
import { TmsBusinessTypeEnum, TmsOperationTypeEnum } from '../../../../../enums/TmsEnum';
import ContainerInfoTable, { type ContainerInfoRow } from './components/ContainerInfoTable';
import BagInfoTable, { type BagInfoRow } from './components/BagInfoTable';
import { useContainerInfoRender, UNIT_TO_CONTAINER_TYPE, CONTAINER_TYPE_TO_UNIT } from './hooks/useContainerInfoRender';
import {
    GET_TMS_LOADING_DETAIL_SUMMARY,
    type GetTmsLoadingDetailSummaryResponse,
    type GetTmsLoadingDetailSummaryVariables,
} from '../../graphql/queries/getTmsLoadingDetailSummary';
import {
    GET_TMS_WAYPOINT_LOADING_INFO_LIST,
    type GetTmsWaypointLoadingInfoListResponse,
    type GetTmsWaypointLoadingInfoListVariables,
} from '../../graphql/queries/getTmsWaypointLoadingInfoList';
import { translateBackendError } from './utils/errorMessage';

interface LoadingDetailProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    onOpenLoadingDetailDrawer?: (waypointId: number) => void;
    task?: TmsTaskDetailType;
}

const LoadingDetail: React.FC<LoadingDetailProps> = ({ waypoint, stepDetailAllowEdit, onOpenLoadingDetailDrawer, task }) => {
    const [loadingTimeDisplay, setLoadingTimeDisplay] = useState<OptionalString>(
        formatTimeDisplay(waypoint.loadingTime)
    );
    const [loadingTimeTimezone] = useState<OptionalString>(waypoint.loadingTimeTimezone);
    const [interiorPhotoUrls, setInteriorPhotoUrls] = useState<string[]>(
        waypoint.loadingInfoInteriorPhotosUrlsJson || []
    );

    const [loadingTimeDraft, setLoadingTimeDraft] = useState<Dayjs | null>(
        waypoint.loadingTime ? dayjs(waypoint.loadingTime) : null
    );
    const [photosDraft, setPhotosDraft] = useState<string[]>(waypoint.loadingInfoInteriorPhotosJson || []);
    const [remarkDraft, setRemarkDraft] = useState<string>(waypoint.loadingInfoRemark || '');
    const [containerRowsDraft, setContainerRowsDraft] = useState<ContainerInfoRow[]>([]);
    const [bagRowsDraft, setBagRowsDraft] = useState<BagInfoRow[]>([]);
    const [isBagTableEditing, setIsBagTableEditing] = useState(false);
    const bagRowsSnapshotRef = React.useRef<BagInfoRow[] | null>(null);

    const taskWaypointId = Number(waypoint.id);
    const { containerDestinationOptions, parcelCount, warehouseIdBySortingCode } = useContainerInfoRender({
        waypoint,
        task,
        containerSource: TmsOperationTypeEnum.Loading,
    });

    const { data: loadingDetailSummaryData, loading: loadingDetailSummaryLoading } = useQuery<
        GetTmsLoadingDetailSummaryResponse,
        GetTmsLoadingDetailSummaryVariables
    >(GET_TMS_LOADING_DETAIL_SUMMARY, {
        variables: { taskId: waypoint.taskId, taskWayPointId: taskWaypointId },
    });

    const containerSummaryRows = React.useMemo((): ContainerInfoRow[] => {
        const groups = loadingDetailSummaryData?.getTmsLoadingDetailSummaryForPortal?.destinationGroups ?? [];
        const rows: ContainerInfoRow[] = [];
        let keyIndex = 0;
        groups.forEach((group) => {
            if (group.containers.length > 0) {
                group.containers.forEach((container) => {
                    rows.push({
                        key: `container-${keyIndex++}`,
                        destination: container.destinationSortingCode ?? group.destinationSortingCode ?? '',
                        quantity: container.containerCount,
                        unit: container.containerType != null ? (CONTAINER_TYPE_TO_UNIT[container.containerType] ?? 'PALLET_BOX') : '',
                    });
                });
            } else {
                // 当前目的地无容器信息，插入一条空行供编辑
                rows.push({
                    key: `container-${keyIndex++}`,
                    destination: group.destinationSortingCode ?? '',
                    quantity: 0,
                    unit: '',
                });
            }
        });
        return rows;
    }, [loadingDetailSummaryData]);

    const { data: loadingInfoListData, loading: loadingInfoListLoading } = useQuery<
        GetTmsWaypointLoadingInfoListResponse,
        GetTmsWaypointLoadingInfoListVariables
    >(GET_TMS_WAYPOINT_LOADING_INFO_LIST, {
        variables: { taskId: waypoint.taskId, taskWaypointId },
    });

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setLoadingTimeDraft(waypoint.loadingTime ? dayjs(waypoint.loadingTime) : null);
            setPhotosDraft(waypoint.loadingInfoInteriorPhotosJson || []);
            setRemarkDraft(waypoint.loadingInfoRemark || '');
            setContainerRowsDraft(containerSummaryRows);
            // 进入编辑时保存快照，用于保存前计算本次新增/删除
            bagRowsSnapshotRef.current = bagRowsDraft.map((r) => ({ ...r }));
        }, [waypoint.loadingTime, waypoint.loadingInfoInteriorPhotosJson, waypoint.loadingInfoRemark, containerSummaryRows, bagRowsDraft]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [saveLoadingDetailMutation] = useMutation(SAVE_TMS_LOADING_DETAIL_INFO);

    const loadingTimeLabel = t('tms.task.drawer.loadingTime');
    const photoLabel = t('tms.task.drawer.loadingInteriorPhotos');
    const loadingInfoLabel = t('tms.task.drawer.loadingInfo');
    const parcelCountLabel = t('tms.task.drawer.loadingInfoParcelCount');
    const containerCountLabel = t('tms.task.drawer.loadingInfoContainerCount');
    const containerInfoLabel = t('tms.task.drawer.loadingInfoContainerInfo');
    const bagInfoLabel = t('tms.task.drawer.loadingInfoBagInfo');
    const remarkLabel = t('common.remark');

    const { renderTimeRow, renderPhotoRow, renderRemarkRow } = useStepDetailRender({ isEditing });

    useEffect(() => {
        setBagRowsDraft([]);
    }, [taskWaypointId]);

    useEffect(() => {
        const list = loadingInfoListData?.getTmsWaypointLoadingInfoList ?? [];
        if (list.length === 0) return;
        const rows: BagInfoRow[] = list.filter((item) => !item.isRemoved).map((item, idx) => ({
            key: `bag-${idx}-${item.barCode}`,
            bagNumber: item.barCode,
            destinationSortingCode: item.destinationSortingCode ?? '',
            parcelCount: item.parcelQuantity,
            isBag: item.isBag,
            validated: true,
        }));
        setBagRowsDraft(rows);
    }, [loadingInfoListData?.getTmsWaypointLoadingInfoList]);

    const resetBagRowsDraft = useCallback(() => {
        const list = loadingInfoListData?.getTmsWaypointLoadingInfoList ?? [];
        const rows: BagInfoRow[] = list.filter((item) => !item.isRemoved).map((item, idx) => ({
            key: `bag-${idx}-${item.barCode}`,
            bagNumber: item.barCode,
            destinationSortingCode: item.destinationSortingCode ?? '',
            parcelCount: item.parcelQuantity,
            isBag: item.isBag,
            validated: true,
        }));
        setBagRowsDraft(rows);
    }, [loadingInfoListData?.getTmsWaypointLoadingInfoList]);

    // 已移出的包牌/运单（任务取消、PDA 单包移出、PC 装车页移出）：表格最下面划线展示，不进编辑草稿、不计运单数量、不参与保存
    const removedBagRows = React.useMemo((): BagInfoRow[] => {
        const list = (loadingInfoListData?.getTmsWaypointLoadingInfoList ?? []).filter((item) => item.isRemoved);
        // key 加 removed 前缀：同一条码移出后又装回时，当前行与划线行不撞 key
        return list.map((item, idx) => ({
            key: `removed-${idx}-${item.barCode}`,
            bagNumber: item.barCode,
            destinationSortingCode: item.destinationSortingCode ?? '',
            parcelCount: item.parcelQuantity,
            isBag: item.isBag,
            validated: true,
        }));
    }, [loadingInfoListData?.getTmsWaypointLoadingInfoList]);

    const handleCancelAndReset = useCallback(() => {
        resetBagRowsDraft();
        bagRowsSnapshotRef.current = null;
        handleCancel();
    }, [handleCancel, resetBagRowsDraft]);

    // 编辑模式下：包牌/运单的目的中心变化时，同步 containerRowsDraft
    useEffect(() => {
        if (!isEditing) return;
        const bagDestinations = new Set(
            bagRowsDraft
                .filter((r) => r.validated && r.destinationSortingCode?.trim())
                .map((r) => String(r.destinationSortingCode).trim())
        );
        setContainerRowsDraft((prev) => {
            const containerDestinations = new Set(prev.map((r) => r.destination).filter(Boolean));
            const toAdd = [...bagDestinations].filter((d) => !containerDestinations.has(d));
            const toRemove = new Set([...containerDestinations].filter((d) => !bagDestinations.has(d)));
            if (toAdd.length === 0 && toRemove.size === 0) return prev;
            let updated = prev.filter((row) => !row.destination || !toRemove.has(row.destination));
            toAdd.forEach((dest, i) => {
                updated = [...updated, {
                    key: `container-sync-${Date.now()}-${i}`,
                    destination: dest,
                    quantity: 0,
                    unit: 'PALLET_BOX',
                }];
            });
            return updated;
        });
    }, [bagRowsDraft, isEditing]);

    useEffect(() => {
        setLoadingTimeDisplay(formatTimeDisplay(waypoint.loadingTime));
        setLoadingTimeDraft(waypoint.loadingTime ? dayjs(waypoint.loadingTime) : null);
    }, [waypoint.loadingTime]);

    useEffect(() => {
        setInteriorPhotoUrls(waypoint.loadingInfoInteriorPhotosUrlsJson || []);
    }, [waypoint.loadingInfoInteriorPhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setPhotosDraft(waypoint.loadingInfoInteriorPhotosJson || []);
            setRemarkDraft(waypoint.loadingInfoRemark || '');
        }
    }, [waypoint.loadingInfoInteriorPhotosJson, waypoint.loadingInfoRemark, isEditing]);

    const renderBagRowChanges = (rows: BagInfoRow[]) => {
        if (rows.length === 0) return null;
        return (
            <div className="mt-1 space-y-1 text-gray-700">
                {rows.map((row) => (
                    <div key={row.bagNumber} className="text-sm" style={{ textIndent: '1rem' }}>
                        {t('tms.task.drawer.loadingChangeConfirmDetail', {
                            bagNumber: row.bagNumber || '-',
                            type: row.isBag
                                ? t('tms.task.drawer.loadingChangeConfirmTypeBag')
                                : t('tms.task.drawer.loadingChangeConfirmTypeParcel'),
                            count: row.parcelCount ?? 0
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const computeBagRowDiffForConfirm = () => {
        const snapshot = bagRowsSnapshotRef.current ?? [];
        const snapshotValidated = snapshot.filter((r) => r.validated);
        const currentValidated = bagRowsDraft.filter((r) => r.validated);

        const snapshotMap = new Map<string, BagInfoRow>();
        snapshotValidated.forEach((r) => snapshotMap.set(r.bagNumber, r));

        const currentMap = new Map<string, BagInfoRow>();
        currentValidated.forEach((r) => currentMap.set(r.bagNumber, r));

        const added: BagInfoRow[] = [];
        const removed: BagInfoRow[] = [];

        currentMap.forEach((row, bagNumber) => {
            if (!snapshotMap.has(bagNumber)) added.push(row);
        });
        snapshotMap.forEach((row, bagNumber) => {
            if (!currentMap.has(bagNumber)) removed.push(row);
        });

        return { added, removed };
    };

    const bagRowsSnapshotByBarCode = React.useMemo(() => {
        const snapshot = bagRowsSnapshotRef.current ?? [];
        const map: Record<string, BagInfoRow> = {};
        snapshot.forEach((r) => {
            const key = (r.bagNumber || '').trim();
            if (!key) return;
            map[key] = r;
        });
        return map;
    }, [isEditing]);

    const isBagEditDisabled = task?.businessType === TmsBusinessTypeEnum.TikTok
        || task?.businessType === TmsBusinessTypeEnum.TemuGA
        || task?.businessType === TmsBusinessTypeEnum.TemuSCF;

    const parcelCountDisplay = React.useMemo(() => {
        const bagSum = bagRowsDraft.filter((r) => r.validated).reduce((sum, r) => sum + (r.parcelCount || 0), 0);
        // 编辑态以草稿为准：包牌删光即显示 0，不回退到已保存的子表统计值
        if (isEditing) return bagSum;
        return bagSum > 0 ? bagSum : parcelCount;
    }, [bagRowsDraft, parcelCount, isEditing]);

    const containerCountDisplay = React.useMemo(() => {
        const rows = isEditing ? containerRowsDraft : containerSummaryRows;
        return rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
    }, [isEditing, containerRowsDraft, containerSummaryRows]);

    const confirmBagRowChangesIfNeeded = async (): Promise<boolean> => {
        if (!isEditing) return true;
        const { added, removed } = computeBagRowDiffForConfirm();
        if (added.length === 0 && removed.length === 0) return true;

            return await new Promise<boolean>((resolve) => {
                Modal.confirm({
                    title: t('tms.task.drawer.loadingChangeConfirmTitle'),
                    content: (
                        <div className="space-y-2 text-sm">
                            <div className="text-sm text-gray-700">{t('tms.task.drawer.loadingChangeConfirmLead')}</div>
                            {added.length > 0 && (
                                <div>
                                    <div className="font-medium">{t('tms.task.drawer.loadingChangeConfirmAdded')}</div>
                                    {renderBagRowChanges(added)}
                                </div>
                            )}
                            {removed.length > 0 && (
                                <div>
                                    <div className="font-medium">{t('tms.task.drawer.loadingChangeConfirmRemoved')}</div>
                                    {renderBagRowChanges(removed)}
                                </div>
                            )}
                            <div className="pt-2 text-gray-500">{t('tms.task.drawer.loadingChangeConfirmHint')}</div>
                        </div>
                    ),
                    okText: t('tms.task.drawer.loadingChangeConfirmOk'),
                    cancelText: t('tms.task.drawer.loadingChangeConfirmCancel'),
                    okButtonProps: { style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } },
                    cancelButtonProps: { style: { color: '#722ed1', borderColor: '#722ed1' } },
                    width: 560,
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
    };

    const validateSave = () => {
        if (isBagTableEditing) {
            message.error(t('tms.task.drawer.bagTableEditingNotSaved'));
            return false;
        }
        if (!loadingTimeDraft) {
            message.error(t('tms.task.drawer.loadingTimeRequired'));
            return false;
        }
        // 容器信息必填
        if (containerRowsDraft.length === 0) {
            message.error(t('tms.task.drawer.loadingContainerRequired'));
            return false;
        }
        // 每一行的目的地、数量、单位都必填
        for (const row of containerRowsDraft) {
            if (!row.destination || !row.destination.trim()) {
                message.error(t('tms.task.drawer.containerDestinationRequired'));
                return false;
            }
            if (row.quantity == null || row.quantity <= 0) {
                message.error(t('tms.task.drawer.containerQuantityRequired'));
                return false;
            }
            if (!row.unit) {
                message.error(t('tms.task.drawer.containerUnitRequired'));
                return false;
            }
        }
        if (photosDraft.length === 0) {
            message.error(t('tms.task.drawer.loadingInteriorPhotosRequired'));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateSave()) return;
        const okToSave = await confirmBagRowChangesIfNeeded();
        if (!okToSave) return;

        const { added, removed } = computeBagRowDiffForConfirm();

        const loadingLocalTime = loadingTimeDraft ? loadingTimeDraft.format('YYYY-MM-DD HH:mm') : null;
        const items = containerRowsDraft.map((row) => {
            const sortingCode = row.destination?.trim() || null;
            const destinationWarehouseId = sortingCode ? (warehouseIdBySortingCode.get(sortingCode) ?? null) : null;
            return {
                destinationWarehouseId,
                sortingCode,
                containerType: row.unit ? UNIT_TO_CONTAINER_TYPE[row.unit] : undefined,
                containerCount: row.quantity,
            };
        });
        const toItemInput = (row: BagInfoRow): HandleBatchTmsLoadingEventItemInput => ({
            isBag: row.isBag,
            barCode: row.bagNumber,
            parcelQuantity: row.isBag ? row.parcelCount : 1,
            destinationWarehouseId: row.destinationSortingCode
                ? (warehouseIdBySortingCode.get(String(row.destinationSortingCode).trim()) ?? null)
                : null,
            destinationSortingCode: row.destinationSortingCode || null,
        });
        const input: SaveTmsLoadingDetailInfoInput = {
            taskId: waypoint.taskId,
            taskWaypointId: taskWaypointId,
            warehouseId: waypoint.waypointId,
            items,
            loadingInfoRemark: remarkDraft || null,
            loadingInfoInteriorPhotosJson: photosDraft,
            loadingLocalTime,
            addedItems: added.map(toItemInput),
            removedItems: removed.map(toItemInput),
        };
        const refetchQueries = [
            { query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } },
            { query: GET_HAS_TMS_LOADING_DETAIL, variables: { taskId: waypoint.taskId, taskWayPointId: taskWaypointId } },
            { query: GET_TMS_LOADING_DETAIL_SUMMARY, variables: { taskId: waypoint.taskId, taskWayPointId: taskWaypointId } },
            { query: GET_TMS_WAYPOINT_LOADING_INFO_LIST, variables: { taskId: waypoint.taskId, taskWaypointId } },
        ];

        try {
            await saveLoadingDetailMutation({
                variables: { input },
                refetchQueries,
                awaitRefetchQueries: true,
            });

            setLoadingTimeDisplay(loadingTimeDraft ? loadingTimeDraft.format('YYYY-MM-DD HH:mm') : null);
            message.success(t('tms.task.drawer.saveSuccess'));
            exitEdit();
            bagRowsSnapshotRef.current = null;
        } catch (error: any) {
            console.error('Failed to save loading detail:', error);
            const details = error?.graphQLErrors?.[0]?.extensions?.details;
            // 重复条码错误：duplicateGroups 为结构化列表，每组含 barCodes、type、canonicalId
            const duplicateGroups = details?.duplicateGroups as Array<{ barCodes: string[]; type: string; canonicalId: string }> | undefined;
            // 目的地聚合错误（details.barCodes 为 ", " 连接的条码串）：表头一行、条码逐行展示
            const barCodes = details?.barCodes as string | undefined;
            const fullMessage = translateBackendError(error);
            const content = duplicateGroups && duplicateGroups.length > 0
                ? (
                    <div style={{ maxWidth: '600px', textAlign: 'left' }}>
                        <div>{fullMessage}</div>
                        {duplicateGroups.map((group, idx) => {
                            const barCodesStr = group.barCodes.join(', ');
                            const groupKey = group.type === 'order'
                                ? 'errors.business.tms.loadingDetail.duplicateGroupOrder'
                                : 'errors.business.tms.loadingDetail.duplicateGroupBag';
                            return <div key={idx}>{t(groupKey, { barCodes: barCodesStr })}</div>;
                        })}
                    </div>
                )
                : barCodes
                ? (
                    <div style={{ maxWidth: '600px', textAlign: 'left' }}>
                        <div>{fullMessage.replace(barCodes, '').trimEnd()}</div>
                        {barCodes.split(', ').filter(Boolean).map((code) => (
                            <div key={code}>{code}</div>
                        ))}
                    </div>
                )
                : <div style={{ maxWidth: '600px', textAlign: 'left' }}>{fullMessage}</div>;
            message.error({ content });
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit && !loadingDetailSummaryLoading && !loadingInfoListLoading}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancelAndReset}
            />
            <div className="mt-1 space-y-3">
                {/* 装车时间 */}
                {renderTimeRow(
                    loadingTimeLabel,
                    loadingTimeDisplay,
                    loadingTimeTimezone,
                    loadingTimeDraft,
                    setLoadingTimeDraft,
                    true,
                    waypoint.loadingTimeTimezone
                )}
                {/* 装车信息 */}
                <div className="border-b border-gray-100">
                    <div className="text-sm text-gray-500 pb-3">{loadingInfoLabel}:</div>
                    <div className="mt-2 pl-4 space-y-2">
                        {/* 运单数量：优先按表格明细汇总；否则展示后端统计 */}
                        <div className="flex items-center gap-3 min-h-[28px] pb-3">
                            {onOpenLoadingDetailDrawer && !isEditing ? (
                                <Button
                                    type="link"
                                    className="p-0 h-auto text-left"
                                    onClick={() => onOpenLoadingDetailDrawer(taskWaypointId)}
                                >
                                    <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                                    <span className="ml-1">
                                        {parcelCountDisplay === null
                                            ? String(t('tms.task.drawer.loading'))
                                            : (parcelCountDisplay ?? 0)}
                                    </span>
                                </Button>
                            ) : (
                                <>
                                    <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                                    <span className="text-gray-700">
                                        {parcelCountDisplay === null
                                            ? String(t('tms.task.drawer.loading'))
                                            : (parcelCountDisplay ?? 0)}
                                    </span>
                                </>
                            )}
                        </div>
                        {/* 容器数量：与下方容器信息表格实时联动 */}
                        <div className="flex items-center gap-3 min-h-[28px] pb-3">
                            <span className="text-sm text-gray-500 shrink-0">{containerCountLabel}:</span>
                            <span className="text-gray-700">{containerCountDisplay}</span>
                        </div>
                        {/* 容器信息 */}
                        <div className="flex gap-1 items-start">
                            <div className="mr-3 text-sm text-gray-500">{containerInfoLabel}:</div>
                            <div className="flex-1 max-w-[600px]">
                                <ContainerInfoTable
                                    dataSource={!isEditing ? containerSummaryRows : containerRowsDraft}
                                    destinationOptions={containerDestinationOptions}
                                    readOnly={!isEditing}
                                    onChange={isEditing ? setContainerRowsDraft : undefined}
                                    readOnlyDestination={true}
                                />
                            </div>
                        </div>
                        {/* 包牌列表 */}
                        <div className="flex gap-1 items-start">
                            <div className="mr-3 text-sm text-gray-500">{bagInfoLabel}:</div>
                            <div className="flex-1 max-w-[600px]">
                                <BagInfoTable
                                    dataSource={bagRowsDraft}
                                    removedRows={removedBagRows}
                                    taskId={waypoint.taskId}
                                    taskWaypointId={taskWaypointId}
                                    warehouseId={waypoint.waypointId}
                                    routeName={task?.routeName ?? ''}
                                    readOnly={!isEditing || isBagEditDisabled}
                                    isEditing={isEditing}
                                    onChange={isEditing && !isBagEditDisabled ? setBagRowsDraft : undefined}
                                    onEditingStateChange={setIsBagTableEditing}
                                    snapshotRowsByBarCode={bagRowsSnapshotByBarCode}
                                    businessType={task?.businessType}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* 装载凭证(车内照片) */}
                {renderPhotoRow(photoLabel, interiorPhotoUrls, photosDraft, setPhotosDraft, interiorPhotoUrls)}
                {renderRemarkRow(remarkLabel, waypoint.loadingInfoRemark, remarkDraft, setRemarkDraft)}
            </div>
        </div>
    );
};

export default LoadingDetail;
